import { C } from '@ridgewill/brand';
import { LOGIN_URL } from '../lib/auth-client.js';
import { Note, button } from '../components/form.js';
import type { SignInMethods } from '../lib/sign-in-methods.js';

/**
 * Change your password — or set a first one — by going where the rules live.
 *
 * ── IT WAS A FORM, AND THE FORM DID NOT STATE THE RULES ─────────────
 *
 * Three password boxes, a mismatch check, and nothing else. The server refuses
 * a password under twelve characters, one without a digit, one without a
 * special character (RidgeWill/auth#295) and one found in a breach corpus
 * (ADR 0003) — and none of that was on screen. You typed something, pressed
 * Change password, and were told what was wrong afterwards, one rule at a time.
 *
 * The set-password screens in the sign-up and reset flows already do this
 * properly: the rules listed, ticking live as you type, a reveal toggle, and a
 * confirmation that retires itself once the password is visible.
 *
 * ── SO WHY NOT REUSE THAT COMPONENT HERE ────────────────────────────
 *
 * Because it lives in `apps/login`, inside the auth repo, and this is a
 * different repository with no shared package between them. Bringing it over
 * means a SECOND copy of the requirements — and the rules are the server's, so
 * a copy is a thing that drifts. This portal has watched that happen: it is the
 * same argument that put the admin portal's capability list on the server
 * rather than in its bundle.
 *
 * Linking is the version with no copy in it. Jon's call, 2026-08-29.
 *
 * ── AND IT IS NOT THE RESET FLOW EITHER ─────────────────────────────
 *
 * This linked to `/forgot-password` for about an hour. That put the rules in
 * front of people and Jon rejected it, rightly: somebody who KNOWS their
 * password should not have to open their inbox to replace it. The email round
 * trip is what a RESET needs to prove identity, and a live session already
 * proves it.
 *
 * So the login app has its own `/change-password`, session-gated, asking for
 * the current password and showing the rules on the new one. This tab survives
 * it — only other devices are signed out — which is why the copy can say so.
 *
 * ── THE SECTION HAD TO LEARN THAT SOME ACCOUNTS HAVE NO PASSWORD ────
 *
 * The link above went out assuming every account had one. An account created
 * through Google, Microsoft or Apple has no credential row, and
 * `/change-password` reads that row to compare against — so it refuses with
 * `CREDENTIAL_ACCOUNT_NOT_FOUND`. The card was sending those people to a screen
 * that could only fail. Jon found it on 2026-08-29 on his own account.
 *
 * The destination is the same URL either way: the login screen asks
 * `/list-accounts` too and drops the current-password field when there is
 * nothing to ask for. What changes here is only what this card SAYS, so that
 * somebody who has never had a password is not invited to "change" one.
 *
 * ── AND IT DOES NOT NAME THE PROVIDER ────────────────────────────────
 *
 * It did, briefly: "you do not have a password yet, so you sign in with
 * Google". Jon's call on 2026-08-31 — the section directly above this one
 * already lists every provider on the account, so repeating one of them here is
 * the same fact told twice, and told less completely the second time. The card
 * says what is true of the PASSWORD and leaves the providers to the list whose
 * job that is.
 *
 * `methods` is null when the lookup failed. That falls back to the change
 * wording — the common case, and the login screen recovers for the rest.
 */
export function ChangePassword({ methods }: { methods: SignInMethods | null | undefined }) {
  /**
   * Nothing is drawn until the answer is in.
   *
   * The alternative is rendering the change wording and flipping it to "Set a
   * password" a moment later, and a button that relabels itself under the
   * cursor is worse than a line of grey text — this card is three lines, so
   * there is no layout to hold open.
   */
  if (methods === undefined) {
    return <p style={{ margin: 0, color: C.muted }}>Checking&hellip;</p>;
  }

  const setting = methods?.hasPassword === false;

  return (
    <>
      <Note>
        {setting
          ? 'Choosing a password happens on the sign-in site, which checks it against every requirement as you type.'
          : 'Choosing a new password happens on the sign-in site, which checks it against every requirement as you type. You will need your current password.'}
      </Note>

      <p style={{ marginTop: '1rem', lineHeight: 1.5 }}>
        {setting ? (
          <>
            This account does not have a password set, so signing in is only possible through one
            of the sign-in providers. Setting a password adds another way to sign in.
          </>
        ) : (
          <>
            Other devices are signed out, and the access they had to Ridgewill apps is revoked. You
            stay signed in here.
          </>
        )}
      </p>

      {/*
        A plain anchor to another ORIGIN, not a router push and not a fetch. The
        account deployment refuses `/forget-password` — it serves the tightest
        allow-list of the three and sign-in style endpoints are not on it — so
        this has to be a full page navigation to the hosted login. See
        lib/auth-client.ts for why `LOGIN_URL` is a separate origin.

        The session crosses with them: the cookie is scoped to the parent domain,
        so the screen over there sees the same person and does not ask them to
        sign in again.

        `returnTo=account` so Continue brings them back here afterwards, as a
        SLUG resolved server-side from TRUSTED_ORIGINS rather than an origin
        written into this file.
      */}
      <p style={{ marginTop: '1.25rem' }}>
        <a
          href={`${LOGIN_URL}/change-password?returnTo=account`}
          style={{ ...button, display: 'inline-block', textDecoration: 'none' }}
        >
          {setting ? 'Set a password' : 'Change your password'}
        </a>
      </p>
    </>
  );
}
